'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PricingOption {
  id: string;
  name: string;
  description: string;
  features: string[];
  recommended?: boolean;
}

const PricingProcurement = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const pricingOptions: PricingOption[] = [
  {
    id: 'lease',
    name: 'Equipment Lease',
    description: 'Flexible monthly leasing with full service and support included',
    features: [
    'No upfront capital investment required',
    'Monthly payment structure fits operating budgets',
    'Includes all maintenance and software updates',
    '24/7 clinical support and training',
    'Upgrade to latest technology every 3 years',
    'Tax-deductible operating expense']

  },
  {
    id: 'purchase',
    name: 'Direct Purchase',
    description: 'One-time purchase with comprehensive warranty and support',
    features: [
    'Full ownership of equipment',
    '5-year comprehensive warranty',
    'Lifetime software updates included',
    'Priority technical support',
    'Volume discount pricing for multi-site hospitals',
    'Flexible financing options available'],

    recommended: true
  },
  {
    id: 'hybrid',
    name: 'Hybrid Model',
    description: 'Combination of purchase and per-use billing for maximum flexibility',
    features: [
    'Lower upfront equipment cost',
    'Per-treatment usage fees',
    'Scales with patient volume',
    'Predictable monthly costs',
    'Full service and support included',
    'Ideal for growing NICUs in tier 2/3 cities']

  }];


  const procurementSteps = [
  {
    step: 1,
    title: 'Initial Consultation',
    description: 'Schedule a call with our medical device specialists to discuss your NICU needs and patient population.',
    duration: '1-2 days'
  },
  {
    step: 2,
    title: 'Clinical Demo',
    description: 'On-site demonstration with your clinical team, including hands-on training and Q&A session.',
    duration: '1 week'
  },
  {
    step: 3,
    title: 'Proposal & Evaluation',
    description: 'Receive customized pricing proposal and ROI analysis. Review with procurement and clinical teams.',
    duration: '2-3 weeks'
  },
  {
    step: 4,
    title: 'Contract & Approval',
    description: 'Finalize contract terms, complete institutional approval process, and schedule implementation.',
    duration: '2-4 weeks'
  },
  {
    step: 5,
    title: 'Installation & Training',
    description: 'Professional installation, comprehensive staff training, and go-live support.',
    duration: '1-2 weeks'
  }];


  return (
    <section id="implementation" className="py-24 bg-background">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-2xl mb-6">
            <Icon name="CurrencyDollarIcon" size={20} />
            <span className="text-sm font-semibold">Pricing & Procurement</span>
          </div>

          <h2 className="lg:text-5xl font-bold font-heading text-foreground mb-6 text-[32px]">
            Flexible Procurement Options for Your Institution
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We offer multiple acquisition pathways designed to fit your hospital's budget and procurement processes in India and LMICs.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-16">
          {pricingOptions.map((option) =>
          <div
            key={option.id}
            className={`bg-card rounded-2xl p-8 hover:shadow-card transition-all duration-250 ease-out ${
            option.recommended ? 'ring-2 ring-primary' : ''}`
            }>

              {option.recommended &&
            <div className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-3 py-1 rounded-2xl text-sm font-semibold mb-4">
                  <Icon name="StarIcon" size={16} />
                  <span>Most Popular</span>
                </div>
            }

              <h3 className="text-2xl font-bold font-heading text-foreground mb-2">
                {option.name}
              </h3>
              <p className="text-muted-foreground mb-6">{option.description}</p>

              <ul className="space-y-3 mb-6">
                {option.features.map((feature, index) =>
              <li key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </li>
              )}
              </ul>

              <button className="w-full bg-primary text-primary-foreground py-3 rounded-2xl font-semibold hover:bg-primary/90 transition-all duration-250 ease-out">
                Request Quote
              </button>
            </div>
          )}
        </div>

        <div className="bg-surface rounded-lg p-8">
          <h3 className="text-2xl font-bold font-heading text-foreground mb-8 text-center">
            Procurement Process Timeline
          </h3>

          <div className="space-y-6">
            {procurementSteps.map((item, index) =>
            <div key={index} className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    {item.step}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-bold text-foreground">{item.title}</h4>
                    <span className="text-sm text-muted-foreground">{item.duration}</span>
                  </div>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 pt-8 border-t border-border text-center">
            <p className="text-lg text-muted-foreground mb-4">
              Total Implementation Timeline: <span className="font-bold text-foreground">6-10 weeks</span>
            </p>
            <button className="bg-accent text-accent-foreground px-8 py-4 rounded-lg font-cta font-bold text-lg shadow-cta hover:bg-accent/90 transition-all duration-250 ease-out">
              Start Procurement Process
            </button>
          </div>
        </div>
      </div>
    </section>);

};

export default PricingProcurement;